import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { pt } from "date-fns/locale";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";
import {
  fetchOpenClosedExceptions,
  fetchReservationBlockedWeekdays,
} from "../actions/reservationsActions";
import Messages from "./Messages";
import Loader from "./Loader";
import { zonedTimeToUtc, utcToZonedTime, format } from "date-fns-tz";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const parseCssColorToRgb = (value) => {
  if (!value || typeof value !== "string") return null;

  const cleanValue = value.trim();

  const hexMatch = cleanValue.match(/^#([0-9A-Fa-f]{6})$/);
  if (hexMatch) {
    const hex = hexMatch[1];

    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  const rgbMatch = cleanValue.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/,
  );

  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
};

const getRelativeLuminance = ({ r, g, b }) => {
  const normalize = (value) => {
    const channel = value / 255;

    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b);
};

const getColorSettings = (mainColor, widgetDark) => {
  const fallbackMainColor = "#EE754C";
  const safeMainColor = parseCssColorToRgb(mainColor)
    ? mainColor
    : fallbackMainColor;

  const rgb = parseCssColorToRgb(safeMainColor);
  const luminance = rgb ? getRelativeLuminance(rgb) : 0.35;

  const mainColorIsLight = luminance > 0.62;
  const mainColorIsVeryLight = luminance > 0.78;

  return {
    safeMainColor,
    mainColorIsLight,
    mainColorIsVeryLight,

    selectedTextColor: mainColorIsLight ? "#4F5F5B" : "#FFFFFF",

    calendarAccent:
      !widgetDark && mainColorIsVeryLight ? "#4F5F5B" : safeMainColor,

    selectedOutline:
      mainColorIsLight && !widgetDark
        ? "inset 0 0 0 2px rgba(79, 95, 91, 0.28)"
        : "none",
  };
};

const DatePickerWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;

  display: flex;
  align-items: flex-start;
  justify-content: center;

  padding: clamp(0px, 1vw, 6px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;

  color: ${({ $widgetDark }) => ($widgetDark ? "#f6f1e8" : "#4F5F5B")};

  scrollbar-width: thin;
  scrollbar-color: ${({ $mainColor }) => $mainColor} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ $mainColor }) => $mainColor};
    border-radius: 999px;
  }

  .react-datepicker {
    width: min(100%, 360px);
    max-width: 100%;
    height: auto;

    font-size: clamp(11px, 3vw, 14px);

    border: none;
    border-radius: 0;
    box-shadow: none;
    padding: 0;

    background-color: transparent !important;
    color: inherit !important;

    overflow: visible;
  }

  .react-datepicker__month-container,
  .react-datepicker__month,
  .react-datepicker__header {
    width: 100%;
    float: none;
    background-color: transparent !important;
  }

  .react-datepicker__header {
    border-bottom: 1px solid
      ${({ $widgetDark }) =>
        $widgetDark ? "rgba(255,255,255,0.18)" : "rgba(79,95,91,0.18)"};
    padding: clamp(3px, 1vw, 7px) clamp(2px, 1vw, 5px) clamp(3px, 1vw, 6px);
  }

  .react-datepicker__current-month {
    font-size: clamp(13px, 3.5vw, 17px);
    font-weight: 800;
    color: ${({ $calendarAccent }) => $calendarAccent} !important;
    text-align: center;
    margin-bottom: clamp(3px, 1vw, 6px);
  }

  .react-datepicker__day-names {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: clamp(1px, 0.7vw, 3px);
    margin: clamp(3px, 1vw, 6px) 0 0;
  }

  .react-datepicker__day-name {
    width: auto;
    margin: 0;
    color: ${({ $widgetDark }) =>
      $widgetDark ? "rgba(246,241,232,0.85)" : "#7C8885"} !important;
    font-weight: 800;
    font-size: clamp(9px, 2.5vw, 11px);
    text-align: center;
  }

  .react-datepicker__month {
    margin: 0;
    padding: clamp(3px, 1vw, 6px) clamp(2px, 1vw, 5px) 0;
  }

  .react-datepicker__week {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: clamp(1px, 0.7vw, 3px);
    margin-bottom: clamp(1px, 0.7vw, 3px);
  }

  .react-datepicker__day,
  .react-datepicker__day--selectable {
    width: auto;
    height: auto;

    aspect-ratio: 1 / 1;
    min-height: clamp(20px, 7.2vw, 30px);

    line-height: 1;
    margin: 0;

    border-radius: clamp(6px, 2vw, 9px);
    position: relative;

    font-weight: 800;
    font-size: clamp(10px, 2.8vw, 12px);

    display: flex;
    align-items: center;
    justify-content: center;

    color: ${({ $widgetDark }) =>
      $widgetDark ? "#f6f1e8" : "#4F5F5B"} !important;
    background: transparent !important;
  }

  .react-datepicker__day--outside-month {
    color: ${({ $widgetDark }) =>
      $widgetDark
        ? "rgba(246,241,232,0.32)"
        : "rgba(79,95,91,0.45)"} !important;
  }

  .react-datepicker__day--today {
    color: ${({ $calendarAccent }) => $calendarAccent} !important;
    box-shadow: inset 0 0 0 1.5px ${({ $calendarAccent }) => $calendarAccent};
  }

  .react-datepicker__day--selected {
    background-color: ${({ $mainColor }) => $mainColor} !important;
    color: ${({ $selectedTextColor }) => $selectedTextColor} !important;
    border-radius: clamp(6px, 2vw, 9px);
    box-shadow: ${({ $selectedOutline }) => $selectedOutline} !important;
  }

  .react-datepicker__day--keyboard-selected {
    background-color: ${({ $widgetDark }) =>
      $widgetDark
        ? "rgba(255,255,255,0.16)"
        : "rgba(79,95,91,0.12)"} !important;
    color: ${({ $widgetDark }) =>
      $widgetDark ? "#f6f1e8" : "#4F5F5B"} !important;
  }

  .react-datepicker__day:hover,
  .react-datepicker__day--selectable:hover {
    background-color: ${({ $mainColor }) => $mainColor} !important;
    opacity: 1 !important;
    transform: none !important;
    box-shadow: ${({ $selectedOutline }) => $selectedOutline} !important;
    color: ${({ $selectedTextColor }) => $selectedTextColor} !important;
  }

  .react-datepicker__day--selected:hover {
    background-color: ${({ $mainColor }) => $mainColor} !important;
    color: ${({ $selectedTextColor }) => $selectedTextColor} !important;
  }

  .react-datepicker__day--disabled,
  .react-datepicker__day--excluded {
    color: ${({ $widgetDark }) =>
      $widgetDark
        ? "rgba(246,241,232,0.24)"
        : "rgba(79,95,91,0.38)"} !important;
    cursor: not-allowed !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .react-datepicker__day--disabled:hover,
  .react-datepicker__day--excluded:hover {
    color: ${({ $widgetDark }) =>
      $widgetDark
        ? "rgba(246,241,232,0.24)"
        : "rgba(79,95,91,0.38)"} !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  @media (max-height: 520px) {
    padding-top: 0;

    .react-datepicker {
      width: min(100%, 340px);
    }

    .react-datepicker__header {
      padding-top: 2px;
      padding-bottom: 2px;
    }

    .react-datepicker__current-month {
      font-size: 13px;
      margin-bottom: 2px;
    }

    .react-datepicker__day-name {
      font-size: 9px;
    }

    .react-datepicker__month {
      padding-top: 2px;
    }

    .react-datepicker__day,
    .react-datepicker__day--selectable {
      min-height: 20px;
      font-size: 10px;
      border-radius: 6px;
    }
  }
`;
const NavButton = styled.button`
  background-color: transparent;
  border: 1px solid
    ${({ $widgetDark }) =>
      $widgetDark ? "rgba(255, 255, 255, 0.22)" : "rgba(153, 176, 173, 0.4)"};
  border-radius: 8px;
  color: ${({ $calendarAccent }) => $calendarAccent};

  width: clamp(28px, 8vw, 34px);
  height: clamp(28px, 8vw, 34px);

  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ $mainColor }) => $mainColor};
    color: ${({ $selectedTextColor }) => $selectedTextColor};
    box-shadow: ${({ $selectedOutline }) => $selectedOutline};
  }

  &:focus {
    outline: none;
  }

  svg {
    color: currentColor !important;
  }
`;

const normalizeDateString = (str) => {
  if (!str || typeof str !== "string") return null;
  const parts = str.split("-");
  if (parts.length !== 3) return null;
  const [d, m, y] = parts;
  return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
};

const CustomDatePicker = ({
  selectedDate,
  onDateChange,
  id,
  handleCloseDateModal = false,
  mainColor = "#EE754C",
  widgetDark = false,
}) => {
  const dispatch = useDispatch();
  const [currentTimeZone, setCurrentTimeZone] = useState("Europe/London");

  const { safeMainColor, calendarAccent, selectedTextColor, selectedOutline } =
    getColorSettings(mainColor, widgetDark);

  const open_closed_exceptions = useSelector(
    (state) => state.closedOpenExceptionsDatePicker,
  );
  const { openClosedExceptions } = open_closed_exceptions;

  const [closedExceptions, setClosedExceptions] = useState(false);
  const [openExceptions, setOpenExceptions] = useState([]);

  useEffect(() => {
    if (openClosedExceptions) {
      setClosedExceptions(openClosedExceptions.closed_exceptions || []);

      const normalizedOpen =
        openClosedExceptions.open_exceptions
          ?.map(normalizeDateString)
          .filter(Boolean) || [];

      setOpenExceptions(normalizedOpen);
    }
  }, [openClosedExceptions]);

  const reservationBlockedWeekDays = useSelector(
    (state) => state.reservationBlockedWeekDays,
  );
  const {
    blockedWeekdays,
    isLoading,
    error,
    isRestaurantOpen,
    restaurantTimeZone,
  } = reservationBlockedWeekDays;

  useEffect(() => {
    dispatch(fetchOpenClosedExceptions(id));
    dispatch(fetchReservationBlockedWeekdays(id));
  }, [dispatch, id]);

  let blockedWeekdayss = [];
  if (blockedWeekdays !== undefined && blockedWeekdays !== "") {
    blockedWeekdayss = blockedWeekdays
      .split(",")
      .map((str) => parseInt(str.trim()))
      .filter((num) => !isNaN(num));
  }

  useEffect(() => {
    if (restaurantTimeZone) {
      setCurrentTimeZone(restaurantTimeZone.timezone);
    }
  }, [restaurantTimeZone]);

  let closedExceptionss = [];
  let dateObject = null;

  if (
    isRestaurantOpen &&
    isRestaurantOpen.block_day !== "No day needs to be blocked."
  ) {
    let dateParts = isRestaurantOpen.block_day.split("-");
    dateObject = new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2]);
  }

  if (closedExceptions) {
    closedExceptionss = closedExceptions.map((dateStr) => {
      const [day, month, year] = dateStr.split("-").map(Number);
      let blockedDate = new Date(year, month - 1, day);
      return utcToZonedTime(blockedDate, currentTimeZone);
    });
  }

  if (dateObject !== null) {
    closedExceptionss.push(dateObject);
  }

  const isBlocked = (date) => {
    const day = date.getDay();

    const dateStr = `${date.getDate().toString().padStart(2, "0")}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${date.getFullYear()}`;

    const isBlockedDay =
      Array.isArray(closedExceptionss) &&
      closedExceptionss.some((blockedDate) => {
        return (
          blockedDate.getDate() === date.getDate() &&
          blockedDate.getMonth() === date.getMonth() &&
          blockedDate.getFullYear() === date.getFullYear()
        );
      });

    if (isBlockedDay) return true;

    const isNormallyBlockedWeekday = blockedWeekdayss.includes(day);
    const isOpenException = openExceptions.includes(dateStr);

    return isNormallyBlockedWeekday && !isOpenException;
  };

  const getTodayInServerTimeZone = () => {
    const now = new Date();
    const zonedNow = utcToZonedTime(now, currentTimeZone);
    zonedNow.setHours(0, 0, 0, 0);
    return zonedNow;
  };

  const minDate = getTodayInServerTimeZone();

  return (
    <DatePickerWrapper
      $mainColor={safeMainColor}
      $calendarAccent={calendarAccent}
      $selectedTextColor={selectedTextColor}
      $selectedOutline={selectedOutline}
      $widgetDark={widgetDark}
    >
      {isLoading ? (
        <h2>
          <Loader />
        </h2>
      ) : blockedWeekdays ? (
        <DatePicker
          inline
          locale={pt}
          selected={selectedDate}
          dayClassName={(date) => {
            let classNames = "";

            if (isBlocked(date)) {
              classNames += " react-datepicker__day--disabled";
            } else {
              classNames += " react-datepicker__day--selectable";
            }

            return classNames.trim();
          }}
          onChange={(date) => {
            date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
            let dateInUTC = zonedTimeToUtc(date, currentTimeZone);
            onDateChange(dateInUTC);
            if (handleCloseDateModal !== false) {
              handleCloseDateModal();
            }

            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }}
          filterDate={(date) => !isBlocked(date)}
          minDate={minDate}
          renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <NavButton
                $mainColor={safeMainColor}
                $calendarAccent={calendarAccent}
                $selectedTextColor={selectedTextColor}
                $selectedOutline={selectedOutline}
                $widgetDark={widgetDark}
                onClick={decreaseMonth}
              >
                <FontAwesomeIcon icon={faChevronLeft} size="lg" />
              </NavButton>

              <span
                style={{
                  margin: "0 10px",
                  fontSize: "clamp(13px, 3.8vw, 15px)",
                  fontWeight: 750,
                  color: calendarAccent,
                  textTransform: "capitalize",
                  lineHeight: 1.1,
                }}
              >
                {format(date, "MMMM yyyy", { locale: pt })}
              </span>

              <NavButton
                $mainColor={safeMainColor}
                $calendarAccent={calendarAccent}
                $selectedTextColor={selectedTextColor}
                $selectedOutline={selectedOutline}
                $widgetDark={widgetDark}
                onClick={increaseMonth}
              >
                <FontAwesomeIcon icon={faChevronRight} size="lg" />
              </NavButton>
            </div>
          )}
        />
      ) : error ? (
        <h2>
          <Messages variant="danger">{error}</Messages>
        </h2>
      ) : (
        <h2>Nothing to be found</h2>
      )}
    </DatePickerWrapper>
  );
};

export default CustomDatePicker;
